import { toaster } from '@/components/ui/toaster';
import { SUCCESS_FEE_MIGRATION_QUERY_KEY } from '@/hooks/queries/use-success-fee-migration';
import { SUCCESS_FEE_TERMS_QUERY_KEY } from '@/hooks/queries/use-success-fee-terms';
import api from '@/libs/axiosInstance';
import { getApiErrorMessage } from '@/libs/utils/error-utils';
import type {
  AcceptSuccessFeeTermsResult,
  ApiResponse,
  SuccessFeeTermsDocument,
} from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

export interface AcceptSuccessFeeTermsInput {
  /** The locale of the snapshot actually displayed, not the one asked for. */
  locale: string;
  /** The hash of that same snapshot, echoed so the proof names what was read. */
  contentHash: string;
}

/** The two fields an acceptance must echo, read off the document on screen. */
export const acceptanceInputFor = (
  document: SuccessFeeTermsDocument
): AcceptSuccessFeeTermsInput => ({
  locale: document.locale,
  contentHash: document.contentHash,
});

const acceptSuccessFeeTerms = async (
  input: AcceptSuccessFeeTermsInput
): Promise<AcceptSuccessFeeTermsResult> => {
  const response = await api.post<ApiResponse<AcceptSuccessFeeTermsResult>>(
    '/dashboard/legal/success-fee-terms/acceptance',
    input
  );

  if (!response.data.success || !response.data.data) {
    throw Object.assign(
      new Error(response.data.message || 'Failed to accept the success fee terms'),
      { response }
    );
  }

  return response.data.data;
};

/**
 * Records the organization's acceptance of the success-fee terms.
 *
 * The caller passes back the locale and hash of the snapshot it rendered; the
 * API refuses anything else, so a proof can never describe a text nobody saw.
 *
 * Accepting is the first of two steps. Invalidating the migration query is what
 * chains into the second: the billing card wakes from its silent `to_invite`
 * state and shows the Shopify approval it now has the right to start.
 */
export function useAcceptSuccessFeeTerms() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation<AcceptSuccessFeeTermsResult, Error, AcceptSuccessFeeTermsInput>({
    mutationFn: acceptSuccessFeeTerms,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: SUCCESS_FEE_TERMS_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: SUCCESS_FEE_MIGRATION_QUERY_KEY }),
      ]);

      toaster.success({
        title: t('legal.successFeeTerms.accept.success.title'),
        description: t('legal.successFeeTerms.accept.success.description'),
      });
    },
    onError: (error) => {
      toaster.error({
        title: t('legal.successFeeTerms.accept.error'),
        description: getApiErrorMessage(error, t, 'legal.successFeeTerms.accept.error'),
      });
    },
  });
}
